import Link from 'next/link';
import {
  Avatar,
  Box,
  capitalize,
  Collapse,
  Divider,
  Typography,
} from '@mui/material';

import { useRouter } from 'next/router';
import { useResponsive } from '@/hooks/useResponsive';
import dot from 'public/icons/dot.svg';
import hash from 'public/icons/hash.svg';
import { dateDiff } from '@/utils/index';
import CommentList from '@/components/Comments/CommentList/CommentList';

import { CommentsProvider } from '@/components/Comments/CommentContext';
import CommentForm from '@/components/Comments/CommentForm/CommentForm';
import CommentModals from '@/components/Comments/CommentModal/CommentModals';
import LeaveCommentBlock from '@/components/Comments/LeaveCommentBlock/LeaveCommentBlock';

export const CommonComponentPost = ({
  comments,
  link,
  feedContent,
  children,
}): JSX.Element => {
  const isMobile = useResponsive('down', 'md');
  const router = useRouter();
  const { author, title, updatedAt, createdAt, id, contentId, source } =
    feedContent ?? {};
  const commentsSize = comments?.comments?.length || 0;
  const postTitle = title ? capitalize(title) : '';

  const handleToggle = () =>
    comments?.onToggleCollapse?.(comments?.collapsed, id || contentId);

  return (
    <>
      <Box sx={{ backgroundColor: '#F3F4F8', borderRadius: '16px' }}>
        <Box p="16px 32px">
          <Link href={link.url} passHref>
            <Typography variant="h3" component="a" color="#34353E">
              {postTitle}
            </Typography>
          </Link>
          <Box
            display="flex"
            flexDirection={isMobile ? 'column' : 'row'}
            alignItems={isMobile ? 'flex-start' : 'center'}
            gap={2}
          >
            <Box display="flex" alignItems="center" gap={2}>
              <Avatar
                alt={author?.firstName}
                src={author?.image?.url}
                sx={{ width: 30, height: 30 }}
              />

              <Typography variant="body2" component="span" color="#34353E">
                {author?.firstName || 'Author'}
              </Typography>

              <Box display="flex" gap={2}>
                <img alt="" src={dot} />
                <img alt="" src={hash} />

                <Typography
                  noWrap
                  variant="body2"
                  component="a"
                  color="#0A444D"
                  sx={{ textDecoration: 'underline', cursor: 'pointer' }}
                  onClick={() => router.push(`/discover/${source.slug}`)}
                >
                  {link.label}
                </Typography>

                {!isMobile ? <img alt="" src={dot} /> : null}
              </Box>
            </Box>

            <Typography variant="body2" color="#34353E">
              {dateDiff(updatedAt || createdAt)}
            </Typography>
          </Box>
        </Box>

        {children}

        {comments?.show && (
          <>
            <Divider variant="fullWidth" />
            <Collapse in={comments?.collapsed}>
              <LeaveCommentBlock
                collapsed={comments?.collapsed}
                handleToggle={handleToggle}
                commentsSize={commentsSize}
              />
            </Collapse>
            <Collapse in={!(comments?.collapsible && comments?.collapsed)}>
              <CommentsProvider
                comments={comments?.comments}
                contentId={id || contentId}
              >
                <Box mx="24px">
                  <CommentForm onCancelClick={handleToggle} />
                  <Divider variant="fullWidth" />
                  <CommentList />
                  <CommentModals />
                </Box>
              </CommentsProvider>
            </Collapse>
          </>
        )}
      </Box>
    </>
  );
};
